import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import React, { useContext, useEffect } from "react";
import { useFormik } from "formik";
import { LoginFormContext } from "context/LoginFormContext";
import SubmitButton from "components/SubmitButton";
import MDSelectBox from "components/MDSelectBox";
import * as Yup from "yup";
import { Box, InputAdornment, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { updateSettleDeposit } from "Apis/amenity";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MDTextField from "components/MDTextField";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// ── Light-theme token system ──────────────────────────────────────────────────
const t = {
  bg: "#F7F8FA", // modal background
  surface: "#FFFFFF", // card / row background
  border: "#E8EAF0", // dividers & borders
  text: "#1A1D23", // primary text
  textSub: "#6B7280", // secondary / label text
  accent: "#5B5BD6", // indigo accent (icon rings, focus)
  green: "#16A34A", // settled green
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  red: "#DC2626", // not-settled red
  redBg: "#FFF1F2",
  redBorder: "#FECACA",
  amber: "#D97706", // extra charges amber
  amberBg: "#FFFBEB",
  shadow: "0 4px 24px rgba(0,0,0,0.10)",
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: t.bg,
  borderRadius: "10px",
  maxWidth: "560px",
  width: "92%",
  overflow: "hidden",
  maxHeight: "92vh",
  height: "auto",
  boxShadow: t.shadow,
  "&:focus": { outline: "none" },
};

export let statusData = [
  { label: "Settled", value: "1" },
  { label: "Not Settled", value: "0" },
];

const validationSchema = Yup.object().shape({
  settlement_amount: Yup.number().required("Settlement amount is required!"),
  refunded_amount: Yup.number().required("Refunded amount is required!"),
  extra_amount: Yup.number().required("Extra amount is required!"),
  notes: Yup.string().required("Notes is required!"),
  is_settled: Yup.string().required("Status is required!").default("0"),
});

const initialValues = {
  settlement_amount: 0,
  refunded_amount: 0,
  extra_amount: 0,
  notes: "",
  is_settled: 0,
};

// ── Reusable row component ────────────────────────────────────────────────────
const InfoRow = ({ iconBg, icon, label, sublabel, children, isLast }) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: t.surface,
        borderBottom: isLast ? "none" : `1px solid ${t.border}`,
        padding: "14px 16px",
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: t.textSub,
                  lineHeight: 1,
                  mb: "3px",
                }}
              >
                {label}
              </Typography>
              {sublabel && (
                <Typography sx={{ fontSize: "11px", color: t.textSub, opacity: 0.7 }}>
                  {sublabel}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ flexShrink: 0, ml: "12px", display: "flex", justifyContent: "flex-end" }}>
            {children}
          </Box>
        </Grid>
      </Grid>
    </Box>
  </>
);

const SettleDepositForm = ({
  open,
  setOpen,
  settleDepositData,
  setSettleDepositData,
  getOccasionList,
}) => {
  const data = settleDepositData?.original;
  const { setNotification } = useContext(LoginFormContext);

  useEffect(() => {
    if (data) {
      formik?.setValues({
        refunded_amount: data?.deposit_amount,
        settlement_amount:
          data?.extra_amount > 0
            ? data?.settlement_amount
            : data?.total_amount - data?.deposit_amount,
        extra_amount: data?.extra_amount,
        notes: data?.notes,
        is_settled: String(data?.is_settled ?? ""),
      });
    }
  }, [data]);

  const handleSubmit = async (values) => {
    const payload = { ...values, id: data?.id };
    let response = await updateSettleDeposit(payload);
    if (response?.status === 200) {
      getOccasionList();
      setOpen(false);
      setSettleDepositData(null);
      setNotification({
        color: "success",
        icon: "check",
        title: "Success",
        content: response?.data?.message,
        open: true,
      });
    } else {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: response?.data?.message,
        open: true,
      });
    }
  };

  const formik = useFormik({ initialValues, validationSchema, onSubmit: handleSubmit });

  const chargeAmount = formik?.values?.extra_amount;
  useEffect(() => {
    if (chargeAmount !== null) {
      const finalAmount =
        data?.total_amount - formik?.values?.refunded_amount + formik?.values?.extra_amount;
      formik.setFieldValue("settlement_amount", finalAmount);
    }
  }, [chargeAmount]);

  const handleCloseModal = () => {
    setOpen(false);
    setSettleDepositData(null);
  };

  const isSettled = String(formik?.values?.is_settled) === "1";

  // Shared styles for inline number inputs inside rows
  const inlineInputSx = {
    "& .MuiInputBase-root": {
      fontSize: "16px",
      fontWeight: 600,
      color: t.text,
      background: t.bg,
      borderRadius: "8px",
      padding: "6px 10px",
      border: `1.5px solid ${t.border}`,
      "&:hover": { borderColor: t.accent },
      "&.Mui-focused": { borderColor: t.accent, boxShadow: `0 0 0 3px rgba(91,91,214,0.12)` },
    },
    "& .MuiInputBase-input": { textAlign: "right", width: "90px", padding: 0 },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
  };

  return (
    <Modal open={open} onClose={handleCloseModal}>
      <Box sx={style}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            background: t.surface,
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <Typography sx={{ fontSize: "17px", fontWeight: 700, color: t.text }}>
            Settle deposit
          </Typography>
          <Box
            onClick={handleCloseModal}
            sx={{
              cursor: "pointer",
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: t.textSub,
              transition: "background 0.15s",
              "&:hover": { background: t.border, color: t.text },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        {/* ── Scrollable Body ── */}
        <Box sx={{ overflowY: "auto", maxHeight: "calc(92vh - 68px)", padding: "20px 20px 24px" }}>
          <Box component="form" role="form" onSubmit={formik?.handleSubmit}>
            {/* ── Rows card ── */}
            <Box
              sx={{
                background: t.surface,
                border: `1px solid ${t.border}`,
                borderRadius: "14px",
                overflow: "hidden",
                mb: "20px",
              }}
            >
              {/* Total Amount — read only */}
              <InfoRow
                iconBg="linear-gradient(135deg,#5B5BD6 0%,#818CF8 100%)"
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#fff" }} />}
                label="Total Amount"
                sublabel="Original deposit · read only"
              >
                <Typography sx={{ fontSize: "17px", fontWeight: 700, color: t.text }}>
                  ₹ {data?.total_amount ?? 0}
                </Typography>
              </InfoRow>

              {/* Refund Amount — editable */}
              <InfoRow
                iconBg="linear-gradient(135deg,#16A34A 0%,#4ADE80 100%)"
                icon={<ReplayIcon sx={{ fontSize: 18, color: "#fff" }} />}
                label="Refund Amount"
                sublabel="Amount to return to customer"
              >
                <MDInput
                  type="text"
                  name="refunded_amount"
                  variant="standard"
                  value={formik?.values?.refunded_amount}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow only whole numbers
                    if (/^\d*$/.test(value)) {
                      formik.setFieldValue("refunded_amount", value);
                    }
                  }}
                  onBlur={formik?.handleBlur}
                  sx={inlineInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: "13px", color: t.textSub, mr: "2px" }}>
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </InfoRow>

              {/* Extra Charges — editable */}
              <InfoRow
                iconBg="linear-gradient(135deg,#D97706 0%,#FCD34D 100%)"
                icon={<WarningAmberIcon sx={{ fontSize: 18, color: "#fff" }} />}
                label="Extra Charges"
                sublabel="Damages, late fees, etc."
              >
                <MDInput
                  type="text"
                  name="extra_amount"
                  variant="standard"
                  value={formik?.values?.extra_amount}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow only whole numbers
                    if (/^\d*$/.test(value)) {
                      formik.setFieldValue("extra_amount", value);
                    }
                  }}
                  onBlur={formik?.handleBlur}
                  sx={inlineInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontSize: "13px", color: t.textSub, mr: "2px" }}>
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </InfoRow>

              {/* Settlement Amount — read only */}
              <InfoRow
                iconBg="linear-gradient(135deg,#DC2626 0%,#F87171 100%)"
                icon={<ReceiptLongIcon sx={{ fontSize: 18, color: "#fff" }} />}
                label="Settlement Amount"
                sublabel="Auto-calculated · read only"
                isLast
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "green",
                      lineHeight: 1,
                    }}
                  >
                    ₹
                  </Typography>

                  <Typography
                    component="span"
                    sx={{
                      fontSize: "17px",
                      fontWeight: 700,
                      color: "green",
                      lineHeight: 1,
                    }}
                  >
                    {Number(formik?.values?.settlement_amount || 0).toLocaleString("en-IN")}
                  </Typography>

                  <input
                    type="hidden"
                    name="settlement_amount"
                    value={formik?.values?.settlement_amount}
                  />
                </Box>
              </InfoRow>
            </Box>

            {/* Validation errors */}
            {formik?.touched?.refunded_amount && formik?.errors?.refunded_amount && (
              <MDBox sx={{ fontSize: "12px", color: t.red, mb: "8px" }}>
                {formik?.errors?.refunded_amount}
              </MDBox>
            )}
            {formik?.touched?.extra_amount && formik?.errors?.extra_amount && (
              <MDBox sx={{ fontSize: "12px", color: t.red, mb: "8px" }}>
                {formik?.errors?.extra_amount}
              </MDBox>
            )}

            {/* ── Notes ── */}
            <Box sx={{ mb: "16px", padding: "0 16px" }}>
              {/* <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text, mb: "6px" }}>
                Notes <span style={{ color: t.red }}>*</span>
              </Typography> */}
              <MDTextField
                type="text"
                name="notes"
                label="Note"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
                placeholder="Add a note about this settlement..."
                // sx={{
                //   width: "100%",
                //   "& .MuiInputBase-root": {
                //     background: t.surface,
                //     border: `1.5px solid ${t.border}`,
                //     borderRadius: "10px",
                //     padding: "12px 14px",
                //     fontSize: "14px",
                //     color: t.text,
                //     "&:hover": { borderColor: t.accent },
                //     "&.Mui-focused": {
                //       borderColor: t.accent,
                //       boxShadow: `0 0 0 3px rgba(91,91,214,0.12)`,
                //     },
                //   },
                //   "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
                // }}
                // variant="standard"
                multiline
                rows={3}
              />
              {formik?.touched?.notes && formik?.errors?.notes && (
                <MDBox sx={{ fontSize: "12px", color: t.red, mt: "4px" }}>
                  {formik?.errors?.notes}
                </MDBox>
              )}
            </Box>

            {/* ── Status row ── */}
            <Grid
              container
              spacing={2}
              alignItems="flex-end"
              sx={{ mb: "20px", padding: "0 16px" }}
            >
              <Grid item xs={12} md={7}>
                {/* <Typography sx={{ fontSize: "13px", fontWeight: 600, color: t.text, mb: "6px" }}>
                  Settlement status <span style={{ color: t.red }}>*</span>
                </Typography> */}
                <MDSelectBox
                  name="is_settled"
                  defaultValue={
                    statusData?.find((s) => s?.value == formik?.values?.is_settled) || null
                  }
                  options={statusData}
                  handleChange={(value) =>
                    formik?.setFieldValue("is_settled", value ? value?.value : null)
                  }
                  label="Settlement Status"
                  width="100%"
                  multiple={false}
                  onBlur={formik?.handleBlur}
                  required
                />
                {formik?.touched?.is_settled && formik?.errors?.is_settled && (
                  <MDBox sx={{ fontSize: "12px", color: t.red, mt: "4px" }}>
                    {formik?.errors?.is_settled}
                  </MDBox>
                )}
              </Grid>

              {/* Live status pill */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: isSettled ? t.greenBg : t.redBg,
                    border: `1.5px solid ${isSettled ? t.greenBorder : t.redBorder}`,
                    transition: "all 0.2s ease",
                  }}
                >
                  {isSettled ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 17, color: t.green }} />
                  ) : (
                    <AccessTimeIcon sx={{ fontSize: 17, color: t.red }} />
                  )}
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 600, color: isSettled ? t.green : t.red }}
                  >
                    {isSettled ? "Settled" : "Not settled"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* ── Submit ── */}
            <Box sx={{ textAlign: "center" }}>
              <SubmitButton btnText="Update Settlement" type="submit" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SettleDepositForm;
